import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
} from 'axios';
import { toast } from 'react-toastify';
import { isEmpty } from 'utils/helpers';
import { REACT_APP_API_URL } from 'config';
import AuthService from './authService';

export const API_TIMEOUT = 60000;

// Use proxy in development for cookie handling
const getBaseURL = (): string => {
  if (process.env.NODE_ENV === 'development') {
    return '/api';
  }
  return REACT_APP_API_URL || '';
};

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: API_TIMEOUT,
});

// Common response wrapper types
export type ApiResponse<T> = {
  data: T;
  status: number;
};

export type ApiError = {
  error: string;
  status: number;
};

interface ApiRequestConfig extends AxiosRequestConfig {
  onUploadProgress?: (progressEvent: any) => void;
  cancelToken?: CancelToken;
  skipAuthRetry?: boolean;
}

function getTokenExpiration(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    if (typeof decoded.exp === 'number') {
      return decoded.exp * 1000;
    }
  } catch {
    // Invalid token format
  }
  return null;
}

function handleError(error: any): void {
  const errorCode = error.response?.status;

  if (errorCode === 401) {
    const isOnLoginPage = window.location.pathname === '/login';

    if (!isOnLoginPage) {
      toast.error('You are not logged in, please log in to your account');
      AuthService.clearTokens();
      localStorage.clear();
      window.location.replace('/login');
    }
  }
}

async function addAuthorizationHeader(
  headers: Record<string, string>,
): Promise<void> {
  const accessToken = AuthService.getAccessToken();

  if (!accessToken) {
    return;
  }

  const expiration = getTokenExpiration(accessToken);
  const REFRESH_BUFFER_MS = 30 * 1000;

  if (expiration && expiration - REFRESH_BUFFER_MS > Date.now()) {
    headers.Authorization = `Bearer ${accessToken}`;
    return;
  }

  const newAccessToken = await AuthService.refreshAccessToken();

  if (newAccessToken) {
    headers.Authorization = `Bearer ${newAccessToken}`;
  }
}

async function sendRequest(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: any,
  config: ApiRequestConfig = {},
): Promise<AxiosResponse> {
  let apiUrl = url;

  const headers: Record<string, string> = {
    ...(config.headers || {}),
  };

  await addAuthorizationHeader(headers);

  const requestConfig: AxiosRequestConfig = {
    method,
    headers,
    url: apiUrl,
  };

  if (config.onUploadProgress) {
    requestConfig.onUploadProgress = config.onUploadProgress;
  }

  if (config.responseType) {
    requestConfig.responseType = config.responseType;
  }

  if (config.cancelToken) {
    requestConfig.cancelToken = config.cancelToken;
  }

  if (config.withCredentials) {
    requestConfig.withCredentials = config.withCredentials;
  }

  if ((method === 'POST' || method === 'PUT' || method === 'PATCH') && data) {
    requestConfig.data = data;
  } else if (method === 'GET' && data) {
    const queryParams = Object.keys(data)
      .map((key) => !isEmpty(data[key]) && `${key}=${data[key]}`)
      .filter((value) => !!value)
      .join('&');

    if (queryParams) {
      apiUrl += (apiUrl.includes('?') ? '&' : '?') + queryParams;
      requestConfig.url = apiUrl;
    }
  }

  try {
    const response = await axiosInstance(requestConfig);
    return response;
  } catch (error: any) {
    if (error.response?.status === 401 && !config.skipAuthRetry) {
      const newAccessToken = await AuthService.refreshAccessToken();

      if (newAccessToken) {
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `Bearer ${newAccessToken}`,
        };
        return axiosInstance(requestConfig);
      }
    }

    if (!config.skipAuthRetry) {
      handleError(error);
    }
    throw error;
  }
}

async function get(
  url: string,
  data?: any,
  config?: ApiRequestConfig,
): Promise<AxiosResponse> {
  return sendRequest('GET', url, data, config);
}

async function post(
  url: string,
  data?: any,
  config?: ApiRequestConfig,
): Promise<AxiosResponse> {
  return sendRequest('POST', url, data, config);
}

async function remove(
  url: string,
  data?: any,
  config?: ApiRequestConfig,
): Promise<AxiosResponse> {
  return sendRequest('DELETE', url, data, config);
}

async function put(
  url: string,
  data?: any,
  config?: ApiRequestConfig,
): Promise<AxiosResponse> {
  return sendRequest('PUT', url, data, config);
}

async function patch(
  url: string,
  data?: any,
  config?: ApiRequestConfig,
): Promise<AxiosResponse> {
  return sendRequest('PATCH', url, data, config);
}

export { get, post, remove, put, patch };
